import { useContext } from 'react';
import { Box } from '@mui/system';
import useStyles from './styles';
import { Delete } from '@mui/icons-material';
import { FvpConfigContext } from '../../../../Contexts/FvpConfigContext';
import InputConfig from '../../../../Components/InputConfig';
import SwitchBoxInput from '../../../../Components/SwitchInput';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { ACCESS_CONTROL_CONSTANTS } from '../../../../core/constants';
import { Modalv2Context } from '../../../../Contexts/Modalv2Context';
import SplitVisualizerIcon from '../../../../Components/PieChartVisualizer';
import useCommonStyles from '../../../../commonStyles';
import PageHeading from '../../../../Components/PageHeading';
const { SYSTEMS } = ACCESS_CONTROL_CONSTANTS;

/**
 * @name FvpBundleScriptSplit
 * @description This component is an editor used to manage the  fvpBundleScriptSplit configuration at the site level.
 *
 *
 * "fvpBundleScriptSplit": {
 *   "enabled": false,
 *   "splits": [
 *     {
 *       name: '', type : " ", loadVideoJsCustomStyles: false
 *     }
 *   ]
 * }
 *
 *
 * @param {{keyData: 'fvpBundleScriptSplit'}} keyData - A key used to identify the configuration within the fvpConfig object.
 */

const FvpBundleScriptSplit = ({ keyData }) => {
	const classes = useStyles();
	const { fvpConfig, updateFvpConfig } = useContext(FvpConfigContext);
	const { updateModalContent } = useContext(Modalv2Context);
	const commonStyleClasses = useCommonStyles();
	const fvpBundleScriptSplitKey = keyData + '.splits';

	/**
	 * @name addFvpBundleScriptConfig
	 * @description Adds a new split configuration with default values to the fvpBundleScriptSplit property within the fvpConfig object.
	 */
	const addFvpBundleScriptConfig = () => {
		let clonedFvpConfig = structuredClone(fvpConfig);
		clonedFvpConfig.fvpBundleScriptSplit = clonedFvpConfig.fvpBundleScriptSplit || { splits: [] };

		const newSplit = {
			url: '',
			splitPercentage: 0,
			loadVideoJsCustomStyles: true
		};

		clonedFvpConfig.fvpBundleScriptSplit.splits = [
			...clonedFvpConfig.fvpBundleScriptSplit.splits,
			newSplit
		];

		updateFvpConfig(clonedFvpConfig);
	};

	/**
	 * @name updateFvpBundleScriptConfig
	 * @description Updates a specific split configuration in the fvpBundleScriptSplit property of the fvpConfig object.
	 *
	 * @param {number} index - The index of the split configuration to update.
	 * @param {string} property - The property of the split configuration to update.
	 * @param {any} value - The new value to assign to the specified property of splits.
	 */
	const updateFvpBundleScriptConfig = (index, property, value) => {
		let clonedFvpConfig = structuredClone(fvpConfig);
		clonedFvpConfig.fvpBundleScriptSplit = clonedFvpConfig.fvpBundleScriptSplit || { splits: [] };
		const split = clonedFvpConfig.fvpBundleScriptSplit.splits[index];

		if (split) {
			clonedFvpConfig.fvpBundleScriptSplit.splits[index] = { ...split, [property]: value };
		}
		updateFvpConfig(clonedFvpConfig);
	};

	/**
	 * @name deleteFvpBundleScriptConfig
	 * @description Deletes a specific split configuration in the fvpBundleScriptSplit property of the fvpConfig object.
	 *
	 * @param {number} index - The index of the split configuration to delete.
	 */
	const deleteFvpBundleScriptConfig = (id) => {
		let clonedFvpConfig = structuredClone(fvpConfig);
		clonedFvpConfig.fvpBundleScriptSplit = clonedFvpConfig.fvpBundleScriptSplit || { splits: [] };

		const updatedSplits = clonedFvpConfig.fvpBundleScriptSplit.splits.filter(
			(_, index) => index !== id
		);
		clonedFvpConfig.fvpBundleScriptSplit.splits = updatedSplits;

		updateFvpConfig(clonedFvpConfig);
	};

	/**
	 * @name fvpBudleScriptSplitVisualizer
	 * @description Extracts and returns an array of objects containing the split percentages from the `fvpBundleScriptSplit.splits` configuration.
	 *
	 */
	const fvpBudleScriptSplitVisualizer = () => {
		const { fvpBundleScriptSplit } = fvpConfig;
		const data = [];
		if (fvpBundleScriptSplit && fvpBundleScriptSplit.splits) {
			for (let split of fvpBundleScriptSplit.splits) {
				const splitPercentage = split.splitPercentage;

				data.push({
					value: splitPercentage
				});
			}
			return data;
		}

		return [];
	};

	const splitVisualizerData = fvpBudleScriptSplitVisualizer();

	return (
		<>
			<Paper className={commonStyleClasses.mainPagePaperMarginStyle}>
				<PageHeading
					headingName="Fvp Bundle Script Split"
					headingPath={['Dashboard', 'Fvp', 'Fvp Bundle Script Split']}
					description="This page allows you to configure the script split settings for fvp."
				/>
				<Box
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						padding: '6rem 5rem 1.4rem 5rem'
					}}
				>
					<SwitchBoxInput
						containerStyles={{ width: 'fit-content', margin: 0 }}
						system={SYSTEMS.FVP_PLAYER}
						isObjectEditor={false}
						keyData={keyData + '.enabled'}
						label="Enabled"
						disabled={false}
						defaultChecked={false}
						directValue={fvpConfig?.fvpBundleScriptSplit?.enabled}
						updateDirectValue={(updatedValue) => {
							const clonedConfig = structuredClone(fvpConfig);
							clonedConfig.fvpBundleScriptSplit = {
								...clonedConfig.fvpBundleScriptSplit,
								enabled: updatedValue
							};
							updateFvpConfig(clonedConfig);
						}}
					/>
					<SplitVisualizerIcon
						data={splitVisualizerData}
						sumRes={100}
						updateModalContent={updateModalContent}
					/>
				</Box>

				<Grid container spacing={2} style={{ paddingLeft: '3rem' }}>
					{fvpConfig?.fvpBundleScriptSplit?.splits?.map((data, index) => (
						<Grid item sm={4} key={index}>
							<FvpBundleScriptConfigControl
								data={data}
								id={index}
								keyData={fvpBundleScriptSplitKey}
								updateFvpBundleScriptConfig={updateFvpBundleScriptConfig}
								deleteFvpBundleScriptConfig={deleteFvpBundleScriptConfig}
							/>
						</Grid>
					)) ?? null}
					<Grid
						item
						xs={12}
						style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center' }}
					>
						<AddCircleOutlineOutlinedIcon
							color="primary"
							fontSize="medium"
							className={classes.icon}
							onClick={addFvpBundleScriptConfig}
						/>
					</Grid>
				</Grid>
			</Paper>
		</>
	);
};

const FvpBundleScriptConfigControl = ({
	data,
	id,
	keyData,
	updateFvpBundleScriptConfig,
	deleteFvpBundleScriptConfig
}) => {
	const classes = useStyles();

	return (
		<Box
			style={{
				border: '1px solid rgba(0, 0, 0, 0.38)',
				padding: '1.5rem',
				margin: '1rem',
				borderRadius: '4px'
			}}
		>
			<Box style={{ width: '100%' }}>
				<InputConfig
					system={SYSTEMS.FVP_PLAYER}
					isObjectEditor={false}
					keyData={keyData + `${id}.url`}
					label="Url"
					directValue={data.url}
					updateDirectValue={(updatedUrl) => updateFvpBundleScriptConfig(id, 'url', updatedUrl)}
					containerStyles={{
						width: '100%',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center'
					}}
					labelStyles={{
						fontWeight: '600',
						width: '100%',
						padding: 0,
						textAlign: 'left'
					}}
					inputStyles={{ width: '100%', margin: '1rem 0' }}
				/>
			</Box>

			<Box style={{ width: '100%' }}>
				<InputConfig
					isObjectEditor={false}
					system={SYSTEMS.FVP_PLAYER}
					keyData={keyData + `${id}.splitPercentage`}
					label="Split Percentage"
					inputType="number"
					inputStyles={{ width: '100%' }}
					directValue={data.splitPercentage}
					updateDirectValue={(updatedUrl) =>
						updateFvpBundleScriptConfig(id, 'splitPercentage', updatedUrl)
					}
					inputProps={{
						min: '0',
						max: '100',
						step: '1'
					}}
					containerStyles={{
						width: '100%',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center'
					}}
					labelStyles={{
						fontWeight: '600',
						width: '100%',
						padding: 0,
						textAlign: 'left'
					}}
				/>
			</Box>

			<Delete
				color="primary"
				fontSize="medium"
				className={classes.delIcon}
				style={{ marginTop: '1.5rem' }}
				onClick={() => deleteFvpBundleScriptConfig(id)}
			/>
		</Box>
	);
};

export default FvpBundleScriptSplit;
